import React, { useState, useEffect } from 'react';

import {
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { formattedCurrency } from '../../../utils/utils';
import UITablePagination from '../../UI/Table/Pagination/UITablePagination';

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 2),
    textAlign: 'center',
    color: '#94a3b8',
  },
  totalBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: '#f0f7ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  totalLabel: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#64748b',
  },
  totalValue: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#1d4ed8',
  },
  // Mobile card styles
  mobileCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px',
  },
  mobileCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    boxShadow: 'none',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: '#cbd5e1',
    },
  },
  mobileCardContentInner: {
    padding: '12px 14px !important',
    '&:last-child': { paddingBottom: '12px !important' },
  },
  mobileCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  mobileCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px',
  },
  metricLabel: {
    fontSize: '0.68rem',
    color: '#94a3b8',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
}));

const EntriesTable = (children) => {
  const { data, loading, error, totalValue, mobile } = children;
  const classes = useStyles();

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [data]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={32} />
      </div>
    );
  }

  const paginatedData = data
    ? data.slice(
      page * rowsPerPage,
      rowsPerPage === -1 ? undefined : page * rowsPerPage + rowsPerPage,
    )
    : [];

  // Total badge (shared by mobile and desktop)
  const totalBadge = (
    <div className={classes.totalBadge}>
      <Typography className={classes.totalLabel}>Total Entradas:</Typography>
      <Typography className={classes.totalValue}>{formattedCurrency(totalValue)}</Typography>
    </div>
  );

  // Mobile: Card view
  if (mobile) {
    return (
      <>
        {totalBadge}
        {(!data || data.length === 0) ? (
          <div className={classes.emptyState}>
            <Typography variant="body2">Sem entradas encontradas</Typography>
          </div>
        ) : (
          <div className={classes.mobileCardList}>
            {paginatedData.map((value) => (
              <Card key={value.id} className={classes.mobileCard}>
                <CardContent className={classes.mobileCardContentInner}>
                  <div className={classes.mobileCardHeader}>
                    <div>
                      <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                        Mov. #{value.id}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#64748b' }}>
                        {value.productId}
                      </Typography>
                    </div>
                    <Typography variant="body2" style={{ fontWeight: 700, color: '#1d4ed8' }}>
                      {formattedCurrency(value.value)}
                    </Typography>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: 4 }}>
                    {value.supplier && (
                      <div>
                        <Typography className={classes.metricLabel}>Fornecedor</Typography>
                        <Typography variant="caption" style={{ fontWeight: 500, color: '#475569' }}>{value.supplier}</Typography>
                      </div>
                    )}
                    <div>
                      <Typography className={classes.metricLabel}>Qtd</Typography>
                      <Typography variant="caption" style={{ fontWeight: 600, color: '#1e293b' }}>{value.amount}</Typography>
                    </div>
                    <div>
                      <Typography className={classes.metricLabel}>Responsável</Typography>
                      <Typography variant="caption" style={{ fontWeight: 500, color: '#475569' }}>{value.userId}</Typography>
                    </div>
                  </div>
                  <div className={classes.mobileCardRow}>
                    <Typography variant="caption" style={{ color: '#94a3b8' }}>{value.moment}</Typography>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <UITablePagination
          count={data ? data.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </>
    );
  }

  // Desktop: Table view (preserved)
  return (
    <TableContainer component={Paper}>
      {totalBadge}
      <Table aria-label="Tabela de entradas de estoque">
        <TableHead>
          <TableRow>
            <TableCell>Movimentação</TableCell>
            <TableCell>Produto</TableCell>
            <TableCell>Fornecedor</TableCell>
            <TableCell>Quantidade</TableCell>
            <TableCell>Responsável</TableCell>
            <TableCell>Valor</TableCell>
            <TableCell />
            <TableCell>Momento</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedData.map((value) => (
            <TableRow
              tabIndex={-1}
              style={{ cursor: 'context-menu' }}
              hover
              key={value.id}
            >
              <TableCell component="th" scope="row">
                {value.id}
              </TableCell>
              <TableCell component="th" scope="row">
                {value.productId}
              </TableCell>
              <TableCell>{value.supplier}</TableCell>
              <TableCell component="th" scope="row">
                {value.amount}
              </TableCell>
              <TableCell>{value.userId}</TableCell>
              <TableCell>{formattedCurrency(value.value)}</TableCell>
              <TableCell />
              <TableCell component="th" scope="row">
                {value.moment}
              </TableCell>
            </TableRow>
          ))}
          {error && (
            <TableRow>
              <TableCell>Sem resultados encontrados</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <UITablePagination
        count={data ? data.length : 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
};

export default EntriesTable;
